import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { LayoutDashboard, Radio, Map, Users, Tag, BadgePercent, Handshake } from 'lucide-react';
import {
  loadRemoteComponent,
  setReconnectCallback,
  FederatedWrapper,
  FallbackHeaderBar,
  FallbackSidebar,
} from '../federation';
import { loadPermsSDK, usePerm } from '../lib/perms';
import { loadBorneUrlTemplate } from '../lib/borneUrl';
import './Layout.css';

// Lazy-load remote components with fallback
const RemoteHeaderBar = React.lazy(() =>
  loadRemoteComponent('./HeaderBar', FallbackHeaderBar)
);

const RemoteSidebar = React.lazy(() =>
  loadRemoteComponent('./Sidebar', FallbackSidebar)
);

/**
 * Etiquette de menu doublee d'un VRAI lien.
 *
 * La barre laterale vient du hub et rend chaque entree en `<button>`. Or un
 * bouton n'a ni Ctrl+clic, ni clic du milieu, ni menu contextuel « Ouvrir le
 * lien dans un nouvel onglet » : ces gestes sont reserves par le navigateur
 * aux vrais `<a href>`, et ne se simulent pas en JavaScript.
 *
 * On glisse donc un lien transparent qui recouvre toute la ligne (motif
 * « stretched link ») : c'est lui que la souris touche, donc le navigateur
 * offre ses affordances natives. `inset: 0` se cale sur le bouton, qui est
 * `position: relative` dans les deux barres laterales (celle du hub par sa
 * classe `relative`, celle de repli parce qu'on l'a posee expres).
 *
 * Rien a changer cote hub : c'est nous qui lui fournissons `sections`, et
 * `label` peut etre un element React aussi bien qu'une chaine.
 *
 * ⚠️ Barre laterale REPLIEE : l'etiquette n'est plus rendue dans la ligne
 * mais dans l'infobulle, qui est `pointer-events: none` — le lien y est donc
 * inerte et c'est `handleNavigate` qui reprend la main (meme resultat, sans
 * les gestes natifs). Ne PAS forcer `pointerEvents: 'auto'` pour rattraper
 * ce cas : le lien se calerait alors sur l'infobulle, qui est elle-meme
 * positionnee, et laisserait une zone cliquable invisible a droite de la
 * barre.
 */
const LienNouvelOnglet = ({ href, children }) => (
  <>
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${children} (nouvel onglet)`}
      title={`${children} — s'ouvre dans un nouvel onglet`}
      // Sans ceci, le clic remonterait jusqu'au bouton du hub, qui
      // ouvrirait un SECOND onglet par-dessus celui du lien.
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'absolute', inset: 0, zIndex: 1 }}
    />
    {children}
  </>
);

function Layout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [, forceRender] = useState(0);
  const [userPhotoUrl, setUserPhotoUrl] = useState(null);

  const user = auth.user?.profile;

  // Auto-reconnexion : quand le Hub revient, re-render pour charger les vrais composants
  useEffect(() => {
    setReconnectCallback(() => {
      console.info('[Antennes] Hub reconnected, reloading federated components...');
      forceRender((n) => n + 1);
    });
    return () => setReconnectCallback(null);
  }, []);

  // Exposer le token Keycloak pour le HeaderBar fédéré (compteur tâches)
  // + chargement du SDK de permissions (cf. lib/perms.js)
  useEffect(() => {
    if (auth.user?.access_token) {
      window.__KONITYS_AUTH = { token: auth.user.access_token };
      const gw = process.env.REACT_APP_GATEWAY_URL || '';
      if (gw) loadPermsSDK({ token: auth.user.access_token, gatewayUrl: gw });
      // Gabarit d URL des fiches bornes : lu une fois par session.
      loadBorneUrlTemplate();
    }
  }, [auth.user?.access_token]);

  // Tasks SDK is now auto-loaded by the federated header (HeaderBar.jsx)

  // Fetch user photo from gateway (via /api/users/me)
  useEffect(() => {
    const token = auth.user?.access_token;
    if (!token) return;
    const gatewayBase = (process.env.REACT_APP_GATEWAY_URL || '').replace(/\/$/, '');
    if (!gatewayBase) return;
    fetch(`${gatewayBase}/api/users/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        const data = d.data?.data || d.data || d;
        if (data?.photo_nom) {
          setUserPhotoUrl(`${gatewayBase}/uploads/contacts/${data.photo_nom}`);
        }
      })
      .catch(() => {});
  }, [auth.user?.access_token]);

  // Map OIDC profile to HeaderBar user props
  const headerUser = user ? {
    firstName: user.given_name || '',
    lastName: user.family_name || '',
    email: user.email || '',
    username: user.preferred_username || '',
    photoUrl: userPhotoUrl || null,
  } : null;

  // Le menu suit les MÊMES permissions que les routes (cf. App.js). Il était
  // auparavant filtré sur le rôle Keycloak `admin` : un profil pouvait cocher
  // « Voir la liste des antennes » sans que le lien apparaisse jamais, alors que
  // la page était bel et bien accessible par son URL. Une seule source de
  // vérité, sinon on configure des droits qui ne se voient pas.
  const canDashboard = usePerm('pages.dashboard.view');
  const canContacts = usePerm('pages.contacts.view');
  const canCarte = usePerm('pages.carte.view');
  const canTarifs = usePerm('pages.tarifs.view');
  const canApports = usePerm('pages.apports_affaire.view');
  const canPrets = usePerm('pages.prets_bornes.view');
  const canVilles = usePerm('pages.antennes.view');

  const general = [
    canDashboard && { icon: LayoutDashboard, label: 'Tableau de bord', path: '/' },
    canContacts && { icon: Users, label: 'Contacts', path: '/contacts' },
    canCarte && { icon: Map, label: 'Carte', path: '/carte' },
    canTarifs && { icon: Tag, label: 'Tarifs', path: '/tarifs' },
    canApports && {
      icon: BadgePercent,
      label: (
        <LienNouvelOnglet href="/apports-affaire">Apports d&apos;affaire</LienNouvelOnglet>
      ),
      // `label` n'etant plus une chaine, on garde le libelle brut pour les
      // endroits qui en ont besoin tel quel (infobulle de la barre repliee).
      titre: 'Apports d\'affaire',
      path: '/apports-affaire',
    },
    canPrets && { icon: Handshake, label: 'Prêts de bornes', path: '/prets-bornes' },
  ].filter(Boolean);

  // NB : /parametres n'est volontairement pas listé ici — sa route exige EN PLUS
  // le rôle Keycloak `admin` (AdminRoute), donc un profil qui coche la
  // permission n'y accéderait pas. À trancher : soit la route s'aligne sur la
  // permission, soit la permission disparaît du schéma.
  const configuration = [
    canVilles && { icon: Radio, label: 'Villes principales', path: '/antennes' },
  ].filter(Boolean);

  const sections = [
    ...(general.length ? [{ label: 'GÉNÉRAL', items: general }] : []),
    ...(configuration.length ? [{ label: 'CONFIGURATION', items: configuration }] : []),
  ];

  /**
   * Entrées de menu qui s'ouvrent dans un NOUVEL ONGLET.
   *
   * Le composant de barre latérale vient du hub, mais c'est nous qui lui
   * passons `sections` et `onNavigate` : il suffit donc d'intercepter ici, sans
   * toucher une ligne du hub.
   */
  const NOUVEL_ONGLET = ['/apports-affaire'];

  /**
   * Filet de securite du lien ci-dessus : sert quand le vrai `<a>` n'est pas
   * atteignable — barre laterale repliee, activation au clavier sur le bouton,
   * ou barre de repli en mode reduit.
   */

  const handleNavigate = (path) => {
    if (path.startsWith('http')) {
      window.open(path, '_blank');
    } else if (NOUVEL_ONGLET.includes(path)) {
      // `noopener` : la page ouverte ne doit pas pouvoir manipuler celle-ci.
      window.open(path, '_blank', 'noopener');
    } else {
      navigate(path);
    }
  };

  const handleLogout = () => {
    auth.signoutRedirect();
  };

  return (
    <div className={`layout-remote ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Header - full width top */}
      <FederatedWrapper
        fallback={
          <FallbackHeaderBar
            currentAppName="Antennes Selfizee"
            currentAppColor="#fb8a2d"
            user={headerUser}
            onLogout={handleLogout}
          />
        }
      >
        <RemoteHeaderBar
          key={auth.user?.access_token ? 'authed' : 'anon'}
          user={headerUser}
          onLogout={handleLogout}
          currentAppName="Antennes Selfizee"
          currentAppIcon="radio"
          currentAppColor="#fb8a2d"
          onNavigate={handleNavigate}
          platformUrl={process.env.REACT_APP_PLATEFORM_URL || ''}
        />
      </FederatedWrapper>

      <div className="layout-body">
        {/* Sidebar - left */}
        <FederatedWrapper
          fallback={
            <FallbackSidebar
              sections={sections}
              activePath={location.pathname}
              onNavigate={handleNavigate}
              collapsed={sidebarCollapsed}
              onCollapse={(collapsed) => setSidebarCollapsed(collapsed)}
            />
          }
        >
          <RemoteSidebar
            sections={sections}
            activePath={location.pathname}
            onNavigate={handleNavigate}
            collapsed={sidebarCollapsed}
            onCollapse={(collapsed) => setSidebarCollapsed(collapsed)}
            platformUrl={process.env.REACT_APP_PLATEFORM_URL || ''}
          />
        </FederatedWrapper>

        {/* Main content - right */}
        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
